import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Loader2, CheckCircle2, AlertCircle, Briefcase, GraduationCap, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';

const CASTE_CATEGORIES = ['Open', 'SC', 'ST', 'VJ', 'NT', 'SBC', 'OBC'];
const PHD_OPTIONS = ['Not Applicable', 'Pursuing', 'Completed'];

const emptyQual = () => ({ degree_branch: '', university_board: '', year_of_passing: '', marks_percent: '', college_name: '', class: '' });
const emptyExp = () => ({ employer: '', designation: '', from_date: '', to_date: '', gross_salary: '', reason_for_leaving: '', place: '' });

const initBio = (schema) => ({
  name: '', email: '', phone1: '', phone2: '',
  address: '', post_applied: '', department: '',
  religion: '', cast: '', caste_category: 'Open',
  qualifications: [emptyQual()],
  employment_history: [emptyExp()],
  teaching_experience_years: '',
  industrial_experience_years: '',
  total_experience: '',
  current_salary: '', expected_salary: '',
  application_date: new Date().toISOString().slice(0, 10),
  application_place: '',
  declaration: false,
  ...(schema === 'teaching' ? {
    area_of_specialization: '', subjects_diploma: '', subjects_graduate: '',
    phd_status: 'Not Applicable', books_published: 0, intl_journal_papers: 0, natl_journal_papers: 0,
    conference_papers: 0, projects: '', patents: ''
  } : {
    remarks: ''
  })
});

export default function PublicApplication() {
  const { jobCode } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [bio, setBio] = useState(null);
  const [resume, setResume] = useState({ base64: '', name: '', mime: '', size: 0 });

  const schema = job?.schema || 'teaching';
  const allowedDepts = useMemo(() => {
    if (!job) return [];
    return (job.departments && job.departments.length) ? job.departments : (job.department ? [job.department] : []);
  }, [job]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/recruitment/public/jobs/${jobCode}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || `Unable to load job (HTTP ${res.status})`);
        }
        const j = await res.json();
        setJob(j);
        const initial = initBio(j.schema);
        // Prefill from job posting
        initial.post_applied = j.designation || j.title || '';
        const depts = (j.departments && j.departments.length) ? j.departments : (j.department ? [j.department] : []);
        if (depts.length === 1) initial.department = depts[0];
        setBio(initial);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, [jobCode]);

  const updateBio = (patch) => setBio(prev => ({ ...prev, ...patch }));

  const updateQualRow = (idx, patch) => {
    setBio(prev => ({ ...prev, qualifications: prev.qualifications.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
  };
  const addQualRow = () => setBio(prev => ({ ...prev, qualifications: [...prev.qualifications, emptyQual()] }));
  const removeQualRow = (idx) => setBio(prev => ({ ...prev, qualifications: prev.qualifications.filter((_, i) => i !== idx) }));

  const updateExpRow = (idx, patch) => {
    setBio(prev => ({ ...prev, employment_history: prev.employment_history.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
  };
  const addExpRow = () => setBio(prev => ({ ...prev, employment_history: [...prev.employment_history, emptyExp()] }));
  const removeExpRow = (idx) => setBio(prev => ({ ...prev, employment_history: prev.employment_history.filter((_, i) => i !== idx) }));

  const validate = () => {
    if (!bio.name?.trim()) return 'Name is required';
    if (!bio.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bio.email)) return 'Valid email is required';
    if (!bio.phone1?.trim() || !/^[0-9+\-\s]{7,15}$/.test(bio.phone1)) return 'Valid phone number is required';
    if (!bio.post_applied?.trim()) return 'Post Applied is required';
    if (allowedDepts.length > 1 && !bio.department?.trim()) return 'Please select a department';
    if (allowedDepts.length > 1 && !allowedDepts.includes(bio.department)) return 'Selected department is not valid for this post';
    if (!bio.declaration) return 'Please confirm the declaration';
    return null;
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setResume({ base64: '', name: '', mime: '', size: 0 }); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Resume must be 5 MB or smaller'); e.target.value = ''; return; }
    if (file.type && file.type !== 'application/pdf') { toast.error('Resume must be a PDF'); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const b64 = (typeof result === 'string' && result.includes(',')) ? result.split(',')[1] : result;
      setResume({ base64: b64, name: file.name, mime: file.type || 'application/pdf', size: file.size });
    };
    reader.onerror = () => toast.error('Could not read the file');
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const cleanQuals = bio.qualifications.filter(q => q.degree_branch?.trim());
      const cleanExps = bio.employment_history.filter(x => x.employer?.trim() || x.designation?.trim());
      const payload = {
        biodata: {
          ...bio,
          qualifications: cleanQuals,
          employment_history: cleanExps,
          email: bio.email.trim().toLowerCase(),
        },
        selected_department: bio.department || (allowedDepts[0] || ''),
        resume_base64: resume.base64 || '',
        resume_filename: resume.name || '',
        resume_mime: resume.mime || '',
      };
      const res = await fetch(`${API}/api/recruitment/public/jobs/${jobCode}/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSuccess(data);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const headerSubtitle = useMemo(() => {
    if (!job) return '';
    const parts = [job.department, job.designation, job.category].filter(Boolean);
    return parts.join(' · ');
  }, [job]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-2" />
            <h2 className="text-xl font-bold mb-1">Unable to load</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full" data-testid="apply-success">
          <CardContent className="py-10 text-center">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-2xl font-bold mb-1">Application Submitted</h2>
            <p className="text-slate-600 mb-3">Thank you, <span className="font-semibold">{success.candidate_name}</span>. Your application for <span className="font-semibold">{success.job_title}</span> has been received.</p>
            <p className="text-xs text-slate-500">Application ID</p>
            <p className="font-mono text-sm bg-slate-100 px-3 py-1.5 rounded inline-block mt-1">{success.application_id}</p>
            <p className="text-xs text-slate-500 mt-4">Save this Application ID for tracking. HR will contact you for next steps.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="mb-4 border-[#DC2626]/30">
          <CardHeader className="bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white rounded-t-lg">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl sm:text-2xl flex items-center gap-2">
                  <Briefcase className="w-6 h-6" /> Sanjay Ghodawat Institute — Application
                </CardTitle>
                <p className="text-sm opacity-90 mt-1">{job.title}</p>
                {headerSubtitle && <p className="text-xs opacity-80">{headerSubtitle}</p>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className="bg-white/20 text-white border-white/40">{job.schema === 'teaching' ? 'Teaching' : 'Non-Teaching'}</Badge>
                {job.wing && <Badge className="bg-white/20 text-white border-white/40 text-[10px]">{job.wing}</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-1 text-sm">
            {job.eligibility && <p><span className="font-semibold text-slate-700">Eligibility:</span> {job.eligibility}</p>}
            {job.pay_scale && <p><span className="font-semibold text-slate-700">Pay Scale:</span> {job.pay_scale}</p>}
            {job.deadline && <p><span className="font-semibold text-slate-700">Last Date:</span> {job.deadline}</p>}
            {job.description && <p className="text-slate-600">{job.description}</p>}
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="public-application-form">
          {/* Personal Info */}
          <Card><CardHeader><CardTitle className="text-base">Applicant Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><Label>Full Name *</Label><Input value={bio.name} onChange={(e) => updateBio({ name: e.target.value })} data-testid="bio-name" /></div>
              <div><Label>Email *</Label><Input type="email" value={bio.email} onChange={(e) => updateBio({ email: e.target.value })} data-testid="bio-email" /></div>
              <div><Label>Phone Primary *</Label><Input value={bio.phone1} onChange={(e) => updateBio({ phone1: e.target.value })} data-testid="bio-phone1" /></div>
              <div><Label>Phone Secondary</Label><Input value={bio.phone2} onChange={(e) => updateBio({ phone2: e.target.value })} data-testid="bio-phone2" /></div>
              <div className="md:col-span-2"><Label>Address</Label><Textarea rows={2} value={bio.address} onChange={(e) => updateBio({ address: e.target.value })} data-testid="bio-address" /></div>
              <div><Label>Post Applied *</Label><Input value={bio.post_applied} onChange={(e) => updateBio({ post_applied: e.target.value })} placeholder={job.designation || job.title} data-testid="bio-post" /></div>
              <div>
                <Label>Department {allowedDepts.length > 1 ? '*' : ''}</Label>
                {allowedDepts.length > 1 ? (
                  <Select value={bio.department || ''} onValueChange={(v) => updateBio({ department: v })}>
                    <SelectTrigger data-testid="bio-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {allowedDepts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={bio.department} onChange={(e) => updateBio({ department: e.target.value })} placeholder={allowedDepts[0] || ''} data-testid="bio-department" readOnly={allowedDepts.length === 1} />
                )}
              </div>
              <div><Label>Religion</Label><Input value={bio.religion} onChange={(e) => updateBio({ religion: e.target.value })} data-testid="bio-religion" /></div>
              <div><Label>Cast</Label><Input value={bio.cast} onChange={(e) => updateBio({ cast: e.target.value })} data-testid="bio-cast" /></div>
              <div>
                <Label>Category</Label>
                <Select value={bio.caste_category} onValueChange={(v) => updateBio({ caste_category: v })}>
                  <SelectTrigger data-testid="bio-caste-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CASTE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Qualifications */}
          <Card><CardHeader><CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Qualifications (Highest first)</span>
            <Button type="button" size="sm" variant="outline" onClick={addQualRow} data-testid="add-qual-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
          </CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr><th className="p-1.5 text-left">Degree & Branch</th><th className="p-1.5 text-left">University/Board</th><th className="p-1.5 text-left">Year</th><th className="p-1.5 text-left">Marks %</th><th className="p-1.5 text-left">College</th><th className="p-1.5 text-left">Class</th><th></th></tr>
                  </thead>
                  <tbody>
                    {bio.qualifications.map((q, i) => (
                      <tr key={i}>
                        <td className="p-1"><Input value={q.degree_branch} onChange={(e) => updateQualRow(i, { degree_branch: e.target.value })} data-testid={`qual-${i}-degree`} /></td>
                        <td className="p-1"><Input value={q.university_board} onChange={(e) => updateQualRow(i, { university_board: e.target.value })} data-testid={`qual-${i}-uni`} /></td>
                        <td className="p-1"><Input value={q.year_of_passing} onChange={(e) => updateQualRow(i, { year_of_passing: e.target.value })} data-testid={`qual-${i}-year`} /></td>
                        <td className="p-1"><Input value={q.marks_percent} onChange={(e) => updateQualRow(i, { marks_percent: e.target.value })} data-testid={`qual-${i}-marks`} /></td>
                        <td className="p-1"><Input value={q.college_name} onChange={(e) => updateQualRow(i, { college_name: e.target.value })} data-testid={`qual-${i}-college`} /></td>
                        <td className="p-1"><Input value={q.class} onChange={(e) => updateQualRow(i, { class: e.target.value })} data-testid={`qual-${i}-class`} /></td>
                        <td className="p-1">{bio.qualifications.length > 1 && <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeQualRow(i)}><Trash2 className="w-3 h-3" /></Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Resume Upload */}
          <Card>
            <CardHeader><CardTitle className="text-base">Resume Upload</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Label>Upload Resume (PDF, max 5 MB)</Label>
              <Input type="file" accept="application/pdf,.pdf" onChange={handleResumeChange} data-testid="bio-resume" />
              {resume.name ? (
                <p className="text-xs text-emerald-700">
                  Selected: <span className="font-medium">{resume.name}</span> ({(resume.size / 1024).toFixed(1)} KB)
                </p>
              ) : (
                <p className="text-xs text-slate-500">Recommended: attach your latest CV / Resume so HOD and Director can review.</p>
              )}
            </CardContent>
          </Card>

          {/* Experience */}
          <Card><CardHeader><CardTitle className="text-base flex items-center justify-between">
            <span>Experience</span>
            <Button type="button" size="sm" variant="outline" onClick={addExpRow} data-testid="add-exp-btn"><Plus className="w-3 h-3 mr-1" /> Add Row</Button>
          </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {schema === 'non_teaching' && (<div><Label>Total Experience</Label><Input value={bio.total_experience} onChange={(e) => updateBio({ total_experience: e.target.value })} placeholder="e.g. 5 years 2 months" data-testid="bio-total-exp" /></div>)}
                <div><Label>Teaching Experience (yrs)</Label><Input type="number" min="0" step="0.5" value={bio.teaching_experience_years} onChange={(e) => updateBio({ teaching_experience_years: e.target.value })} data-testid="bio-teach-exp" /></div>
                <div><Label>Industrial Experience (yrs)</Label><Input type="number" min="0" step="0.5" value={bio.industrial_experience_years} onChange={(e) => updateBio({ industrial_experience_years: e.target.value })} data-testid="bio-ind-exp" /></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr><th className="p-1.5 text-left">Employer</th><th className="p-1.5 text-left">Designation</th><th className="p-1.5 text-left">From</th><th className="p-1.5 text-left">To</th><th className="p-1.5 text-left">Salary (₹/mo)</th><th className="p-1.5 text-left">Reason</th><th className="p-1.5 text-left">Place</th><th></th></tr>
                  </thead>
                  <tbody>
                    {bio.employment_history.map((x, i) => (
                      <tr key={i}>
                        <td className="p-1"><Input value={x.employer} onChange={(e) => updateExpRow(i, { employer: e.target.value })} data-testid={`exp-${i}-emp`} /></td>
                        <td className="p-1"><Input value={x.designation} onChange={(e) => updateExpRow(i, { designation: e.target.value })} data-testid={`exp-${i}-des`} /></td>
                        <td className="p-1"><Input type="date" value={x.from_date} onChange={(e) => updateExpRow(i, { from_date: e.target.value })} data-testid={`exp-${i}-from`} /></td>
                        <td className="p-1"><Input type="date" value={x.to_date} onChange={(e) => updateExpRow(i, { to_date: e.target.value })} data-testid={`exp-${i}-to`} /></td>
                        <td className="p-1"><Input value={x.gross_salary} onChange={(e) => updateExpRow(i, { gross_salary: e.target.value })} data-testid={`exp-${i}-sal`} /></td>
                        <td className="p-1"><Input value={x.reason_for_leaving} onChange={(e) => updateExpRow(i, { reason_for_leaving: e.target.value })} data-testid={`exp-${i}-reason`} /></td>
                        <td className="p-1"><Input value={x.place} onChange={(e) => updateExpRow(i, { place: e.target.value })} data-testid={`exp-${i}-place`} /></td>
                        <td className="p-1">{bio.employment_history.length > 1 && <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeExpRow(i)}><Trash2 className="w-3 h-3" /></Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Teaching-only section */}
          {schema === 'teaching' && (
            <Card><CardHeader><CardTitle className="text-base">Specialization, Publications & Projects</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="md:col-span-2"><Label>Area of Specialization</Label><Textarea rows={2} value={bio.area_of_specialization} onChange={(e) => updateBio({ area_of_specialization: e.target.value })} data-testid="bio-spec" /></div>
                <div><Label>Subjects Teaching at Diploma Level</Label><Textarea rows={2} value={bio.subjects_diploma} onChange={(e) => updateBio({ subjects_diploma: e.target.value })} data-testid="bio-subj-dip" /></div>
                <div><Label>Subjects Teaching at Graduate Level</Label><Textarea rows={2} value={bio.subjects_graduate} onChange={(e) => updateBio({ subjects_graduate: e.target.value })} data-testid="bio-subj-grad" /></div>
                <div>
                  <Label>Ph.D. Status</Label>
                  <Select value={bio.phd_status} onValueChange={(v) => updateBio({ phd_status: v })}>
                    <SelectTrigger data-testid="bio-phd"><SelectValue /></SelectTrigger>
                    <SelectContent>{PHD_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label># Books Published</Label><Input type="number" min="0" value={bio.books_published} onChange={(e) => updateBio({ books_published: Number(e.target.value) })} data-testid="bio-books" /></div>
                <div><Label># Intl Journal Papers</Label><Input type="number" min="0" value={bio.intl_journal_papers} onChange={(e) => updateBio({ intl_journal_papers: Number(e.target.value) })} data-testid="bio-intl" /></div>
                <div><Label># Natl Journal Papers</Label><Input type="number" min="0" value={bio.natl_journal_papers} onChange={(e) => updateBio({ natl_journal_papers: Number(e.target.value) })} data-testid="bio-natl" /></div>
                <div><Label># Conference Papers</Label><Input type="number" min="0" value={bio.conference_papers} onChange={(e) => updateBio({ conference_papers: Number(e.target.value) })} data-testid="bio-conf" /></div>
                <div className="md:col-span-2"><Label>Projects Carried Out</Label><Textarea rows={2} value={bio.projects} onChange={(e) => updateBio({ projects: e.target.value })} data-testid="bio-projects" /></div>
                <div className="md:col-span-2"><Label>Patents</Label><Textarea rows={2} value={bio.patents} onChange={(e) => updateBio({ patents: e.target.value })} data-testid="bio-patents" /></div>
              </CardContent>
            </Card>
          )}

          {/* Salary + Declaration */}
          <Card><CardHeader><CardTitle className="text-base">Salary & Declaration</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><Label>Current Salary (₹/mo)</Label><Input type="number" min="0" value={bio.current_salary} onChange={(e) => updateBio({ current_salary: e.target.value })} data-testid="bio-cur-sal" /></div>
              <div><Label>Expected Salary (₹/mo)</Label><Input type="number" min="0" value={bio.expected_salary} onChange={(e) => updateBio({ expected_salary: e.target.value })} data-testid="bio-exp-sal" /></div>
              {schema !== 'teaching' && (<div className="md:col-span-2"><Label>Any Other Remark</Label><Textarea rows={2} value={bio.remarks} onChange={(e) => updateBio({ remarks: e.target.value })} data-testid="bio-remarks" /></div>)}
              <div><Label>Date</Label><Input type="date" value={bio.application_date} onChange={(e) => updateBio({ application_date: e.target.value })} data-testid="bio-date" /></div>
              <div><Label>Place</Label><Input value={bio.application_place} onChange={(e) => updateBio({ application_place: e.target.value })} data-testid="bio-place" /></div>
              <div className="md:col-span-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                <input id="declaration" type="checkbox" checked={bio.declaration} onChange={(e) => updateBio({ declaration: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#DC2626]" data-testid="bio-declaration" />
                <label htmlFor="declaration" className="text-xs text-amber-900">I hereby declare that the information provided above is true to the best of my knowledge. I understand that any false statement may lead to rejection of my application.</label>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center sm:justify-end pb-20 sm:pb-4">
            <Button type="submit" disabled={submitting} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 py-6 text-base w-full sm:w-auto max-w-xs shadow-lg" data-testid="submit-application-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit Application
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
